import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format, startOfMonth, endOfMonth, subMonths, startOfYear, endOfYear } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  Users,
  TrendingUp,
  TrendingDown,
  Calendar,
  Filter,
  Cake,
  Phone,
  MailIcon,
  ShoppingCart,
  DollarSign,
  Package,
  GitBranch,
  Activity,
} from 'lucide-react';
import { Card } from '@caruso/ui';
import { Input } from '@caruso/ui';
import { dashboardService } from '@/services/dashboard.service';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

type DateFilter = 'all' | 'thisMonth' | 'lastMonth' | 'last3Months' | 'thisYear' | 'custom';

export default function Dashboard() {
  const [dateFilter, setDateFilter] = useState<DateFilter>('thisMonth');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');

  // Calcular datas baseadas no filtro selecionado
  const getFilterDates = () => {
    const now = new Date();

    switch (dateFilter) {
      case 'all':
        return { startDate: undefined, endDate: undefined };

      case 'thisMonth':
        return {
          startDate: format(startOfMonth(now), 'yyyy-MM-dd'),
          endDate: format(endOfMonth(now), 'yyyy-MM-dd'),
        };

      case 'lastMonth':
        const lastMonth = subMonths(now, 1);
        return {
          startDate: format(startOfMonth(lastMonth), 'yyyy-MM-dd'),
          endDate: format(endOfMonth(lastMonth), 'yyyy-MM-dd'),
        };

      case 'last3Months':
        return {
          startDate: format(subMonths(now, 3), 'yyyy-MM-dd'),
          endDate: format(now, 'yyyy-MM-dd'),
        };

      case 'thisYear':
        return {
          startDate: format(startOfYear(now), 'yyyy-MM-dd'),
          endDate: format(endOfYear(now), 'yyyy-MM-dd'),
        };

      case 'custom':
        return startDate && endDate ? { startDate, endDate } : { startDate: undefined, endDate: undefined };

      default:
        return { startDate: undefined, endDate: undefined };
    }
  };

  const filterDates = getFilterDates();

  const { data: statistics, isLoading } = useQuery({
    queryKey: ['dashboard-statistics', filterDates.startDate, filterDates.endDate],
    queryFn: () => dashboardService.getStatistics(filterDates),
  });

  const { data: chartData } = useQuery({
    queryKey: ['dashboard-charts', filterDates.startDate, filterDates.endDate],
    queryFn: () => dashboardService.getChartData(filterDates),
  });

  const { data: flowExecutionsByStatus } = useQuery({
    queryKey: ['flow-executions-by-status'],
    queryFn: () => dashboardService.getFlowExecutionsByStatus(),
  });

  const handleFilterChange = (value: DateFilter) => {
    setDateFilter(value);
    if (value !== 'custom') {
      setStartDate('');
      setEndDate('');
    }
  };

  const formatPhone = (phone?: string) => {
    if (!phone) return '';
    return phone.replace(/(\d{2})(\d{4,5})(\d{4})/, '($1) $2-$3');
  };

  const GrowthIndicator = ({ rate }: { rate: number }) => {
    const isPositive = rate >= 0;
    return (
      <div className={`flex items-center gap-1 text-sm font-semibold ${isPositive ? 'text-green-400' : 'text-red-400'}`}>
        {isPositive ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
        <span>{Math.abs(rate).toFixed(1)}%</span>
      </div>
    );
  };

  const StatCard = ({
    icon: Icon,
    label,
    value,
    growth,
    iconColor = 'brand-gold'
  }: {
    icon: any;
    label: string;
    value: number;
    growth?: number;
    iconColor?: string;
  }) => (
    <Card className="p-4 sm:p-5 lg:p-7 bg-brand-smoke border-brand-gold/20 hover:border-brand-gold/40 transition-all">
      <div className="flex items-start justify-between mb-3 sm:mb-4">
        <div className={`p-2 sm:p-3 rounded-xl bg-${iconColor}/10 ring-1 ring-${iconColor}/20`}>
          <Icon className={`w-5 h-5 sm:w-6 sm:h-6 text-${iconColor}`} strokeWidth={2} />
        </div>
        {growth !== undefined && (
          <GrowthIndicator rate={growth} />
        )}
      </div>
      <div className="space-y-1 sm:space-y-2">
        <div className="text-xs sm:text-sm lg:text-base font-medium text-brand-ivory uppercase tracking-wide">{label}</div>
        <div className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white">{value.toLocaleString()}</div>
      </div>
    </Card>
  );

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-brand-smoke rounded w-48"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="h-32 bg-brand-smoke rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Preparar dados para gráfico de crescimento
  const growthData = [
    {
      name: 'Clientes',
      Atual: statistics?.growth.clients.current || 0,
      Anterior: statistics?.growth.clients.previous || 0,
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white">Dashboard</h1>
          <p className="text-brand-ivory/80 mt-1 sm:mt-2 text-sm sm:text-base lg:text-lg">
            Visão geral do sistema
          </p>
        </div>
      </div>

      {/* Date Filters */}
      <Card className="p-6 bg-brand-smoke border-brand-gold/20">
        <div className="space-y-4">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex-1 min-w-[200px]">
              <label className="block text-base font-semibold text-white mb-2">
                Período
              </label>
              <div className="relative">
                <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-gold/50 z-10" />
                <select
                  value={dateFilter}
                  onChange={(e) => handleFilterChange(e.target.value as DateFilter)}
                  className="w-full pl-10 pr-4 py-2.5 bg-brand-smoke/50 border border-brand-gold/20 rounded-lg text-brand-ivory focus:outline-none focus:ring-2 focus:ring-brand-gold/50 focus:border-brand-gold/50 appearance-none cursor-pointer"
                  style={{
                    colorScheme: 'dark',
                    color: 'rgb(244, 241, 233) !important' as any,
                    WebkitTextFillColor: 'rgb(244, 241, 233)',
                  }}
                >
                  <option value="thisMonth">Este Mês</option>
                  <option value="lastMonth">Último Mês</option>
                  <option value="last3Months">Últimos 3 Meses</option>
                  <option value="thisYear">Este Ano</option>
                  <option value="all">Todos</option>
                  <option value="custom">Personalizado</option>
                </select>
              </div>
            </div>

            {dateFilter === 'custom' && (
              <>
                <div className="flex-1 min-w-[200px]">
                  <label className="block text-base font-semibold text-white mb-2">
                    Data Inicial
                  </label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-brand-gold/50" />
                    <Input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      className="pl-11 text-base"
                    />
                  </div>
                </div>
                <div className="flex-1 min-w-[200px]">
                  <label className="block text-base font-semibold text-white mb-2">
                    Data Final
                  </label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-brand-gold/50" />
                    <Input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      className="pl-11 text-base"
                    />
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Info sobre o período selecionado */}
          {dateFilter !== 'all' && dateFilter !== 'custom' && (
            <div className="text-base text-brand-ivory/80 bg-brand-ink/30 px-4 py-3 rounded-lg">
              Mostrando dados de{' '}
              <span className="text-brand-gold font-semibold">
                {filterDates.startDate && format(new Date(filterDates.startDate), 'dd/MM/yyyy', { locale: ptBR })}
              </span>
              {' até '}
              <span className="text-brand-gold font-semibold">
                {filterDates.endDate && format(new Date(filterDates.endDate), 'dd/MM/yyyy', { locale: ptBR })}
              </span>
            </div>
          )}
          {dateFilter === 'all' && (
            <div className="text-base text-brand-ivory/80 bg-brand-ink/30 px-4 py-3 rounded-lg">
              Mostrando <span className="text-brand-gold font-semibold">todos os dados</span>
            </div>
          )}
          {dateFilter === 'custom' && startDate && endDate && (
            <div className="text-base text-brand-ivory/80 bg-brand-ink/30 px-4 py-3 rounded-lg">
              Mostrando dados de{' '}
              <span className="text-brand-gold font-semibold">
                {format(new Date(startDate), 'dd/MM/yyyy', { locale: ptBR })}
              </span>
              {' até '}
              <span className="text-brand-gold font-semibold">
                {format(new Date(endDate), 'dd/MM/yyyy', { locale: ptBR })}
              </span>
            </div>
          )}
        </div>
      </Card>

      {/* Overview Statistics */}
      <div>
        <h2 className="text-xl sm:text-2xl font-bold text-white mb-4 sm:mb-6">Visão Geral</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          <StatCard
            icon={Users}
            label="Total de Clientes"
            value={statistics?.overview.totalClients || 0}
            growth={statistics?.growth.clients.rate}
            iconColor="brand-gold"
          />
          <StatCard
            icon={ShoppingCart}
            label="Total de Pedidos"
            value={statistics?.orders.total || 0}
            iconColor="blue-400"
          />
          <StatCard
            icon={DollarSign}
            label="Valor Total Pedidos"
            value={statistics?.orders.totalValue || 0}
            iconColor="emerald-400"
          />
          <StatCard
            icon={DollarSign}
            label="Ticket Médio"
            value={Math.round(statistics?.orders.averageTicket || 0)}
            iconColor="yellow-400"
          />
          <StatCard
            icon={GitBranch}
            label="Fluxos Executados"
            value={statistics?.flows.totalExecutions || 0}
            iconColor="violet-400"
          />
        </div>
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {/* Growth Comparison Chart */}
        <Card className="p-4 sm:p-6 bg-brand-smoke border-brand-gold/20">
          <h3 className="text-lg sm:text-xl font-bold text-white mb-2">Crescimento de Clientes</h3>
          <p className="text-sm sm:text-base text-brand-ivory/70 mb-4 sm:mb-6">Últimos 30 dias vs Período Anterior</p>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={growthData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis dataKey="name" stroke="#9CA3AF" tick={{ fill: '#D1D5DB' }} />
              <YAxis stroke="#9CA3AF" tick={{ fill: '#D1D5DB' }} />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#1F2937',
                  border: '1px solid rgba(212, 175, 55, 0.3)',
                  borderRadius: '8px',
                  color: '#F3F4F6'
                }}
              />
              <Legend wrapperStyle={{ color: '#D1D5DB' }} />
              <Bar dataKey="Atual" fill="#D4AF37" radius={[8, 8, 0, 0]} />
              <Bar dataKey="Anterior" fill="#6B7280" radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        {/* Flow Executions Chart */}
        {flowExecutionsByStatus && flowExecutionsByStatus.length > 0 && (
          <Card className="p-4 sm:p-6 bg-brand-smoke border-brand-gold/20">
            <h3 className="text-lg sm:text-xl font-bold text-white mb-2">Fluxos por Status</h3>
            <p className="text-sm sm:text-base text-brand-ivory/70 mb-4 sm:mb-6">Distribuição de execuções de fluxos</p>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={flowExecutionsByStatus.map((item) => ({
                    name: item.status,
                    value: item.count,
                  }))}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, value }: any) => `${name}: ${value}`}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {flowExecutionsByStatus.map((entry, index) => {
                    const colors = {
                      completed: '#10b981',
                      running: '#3b82f6',
                      paused: '#f59e0b',
                      failed: '#ef4444',
                      cancelled: '#6b7280',
                    };
                    return (
                      <Cell
                        key={`cell-${index}`}
                        fill={colors[entry.status as keyof typeof colors] || '#8b5cf6'}
                      />
                    );
                  })}
                </Pie>
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#1F2937',
                    border: '1px solid rgba(212, 175, 55, 0.3)',
                    borderRadius: '8px',
                    color: '#F3F4F6'
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </Card>
        )}

        {/* Timeline Chart */}
        {chartData && (
          <Card className="p-6 bg-brand-smoke border-brand-dark-lighter border-brand-gold/20">
            <h3 className="text-xl font-bold text-white mb-2">Evolução de Clientes</h3>
            <p className="text-base text-brand-ivory/70 mb-6">Novos clientes ao longo do tempo</p>
            <ResponsiveContainer width="100%" height={350}>
              <AreaChart data={chartData.clients.map((item) => ({
                date: format(new Date(item.date), 'dd/MM', { locale: ptBR }),
                Clientes: item.count,
              }))}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey="date" stroke="#9CA3AF" tick={{ fill: '#D1D5DB' }} />
                <YAxis stroke="#9CA3AF" tick={{ fill: '#D1D5DB' }} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#1F2937',
                    border: '1px solid rgba(212, 175, 55, 0.3)',
                    borderRadius: '8px',
                    color: '#F3F4F6'
                  }}
                />
                <Legend wrapperStyle={{ color: '#D1D5DB' }} />
                <Area type="monotone" dataKey="Clientes" stroke="#D4AF37" fill="#D4AF37" fillOpacity={0.6} />
              </AreaChart>
            </ResponsiveContainer>
          </Card>
        )}
      </div>

      {/* Upcoming Birthdays */}
      {statistics?.upcomingBirthdays && statistics.upcomingBirthdays.length > 0 && (
        <div>
          <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
            <Cake className="w-7 h-7 text-pink-400" />
            Próximos Aniversariantes (15 dias)
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {statistics.upcomingBirthdays.map((client) => (
              <Card
                key={client.id}
                className="p-7 bg-brand-smoke border-brand-gold/20 hover:border-pink-400/60 transition-all relative overflow-hidden"
              >
                {/* Decorative gradient */}
                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-pink-500/10 to-purple-500/10 rounded-full blur-2xl -z-0"></div>

                <div className="relative z-10">
                  {/* Header with badge */}
                  <div className="flex items-start justify-between mb-5">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-pink-500 to-purple-500 flex items-center justify-center">
                        <Cake className="w-6 h-6 text-white" />
                      </div>
                      <div className="flex items-center gap-2 px-4 py-2 bg-pink-500 text-white rounded-full shadow-lg">
                        <span className="text-base font-bold">
                          {client.daysUntilBirthday === 0 ? 'HOJE!' : `${client.daysUntilBirthday} dias`}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Client info */}
                  <div className="mb-5">
                    <h4 className="font-bold text-white text-xl mb-2">{client.name}</h4>
                    <p className="text-lg text-pink-300 font-medium">
                      {format(new Date(client.birthDate), "dd 'de' MMMM", { locale: ptBR })}
                    </p>
                  </div>

                  {/* Contact info */}
                  <div className="space-y-3 pt-5 border-t border-brand-gold/20">
                    {client.email && (
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-brand-gold/10 rounded-lg">
                          <MailIcon className="w-5 h-5 text-brand-gold" />
                        </div>
                        <span className="text-base text-white truncate">{client.email}</span>
                      </div>
                    )}
                    {client.phone && (
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-brand-gold/10 rounded-lg">
                          <Phone className="w-5 h-5 text-brand-gold" />
                        </div>
                        <span className="text-base text-white">{formatPhone(client.phone)}</span>
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Two Columns Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Products */}
        <div>
          <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
            <Package className="w-7 h-7 text-orange-400" />
            Top 5 Produtos da Loja
          </h2>
          <Card className="p-6 bg-brand-smoke border-brand-gold/20">
            {statistics?.topProducts && statistics.topProducts.length > 0 ? (
              <div className="space-y-4">
                {statistics.topProducts.map((product, index) => (
                  <div key={product.id} className="flex items-center justify-between p-5 bg-brand-ink/30 rounded-lg hover:bg-brand-ink/50 transition-colors border border-brand-gold/10">
                    <div className="flex items-center gap-4 flex-1">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-orange-500 to-red-600 flex items-center justify-center shadow-lg">
                        <span className="text-white font-bold text-lg">{index + 1}</span>
                      </div>
                      <div className="flex-1">
                        <div className="text-white font-semibold text-lg">{product.name}</div>
                        <div className="text-sm text-brand-ivory/60 mt-1">
                          {product.quantitySold} unidades vendidas • {product.orderCount} pedidos
                        </div>
                      </div>
                    </div>
                    <div className="text-brand-gold font-bold text-xl">
                      {new Intl.NumberFormat('pt-BR', {
                        style: 'currency',
                        currency: 'BRL',
                      }).format(product.price)}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-brand-ivory/60 text-base">
                <Package className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>Nenhum produto encontrado</p>
              </div>
            )}
          </Card>
        </div>

        {/* Recent Activities */}
        <div>
          <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
            <Activity className="w-7 h-7 text-blue-400" />
            Atividades Recentes
          </h2>
          <Card className="p-6 bg-brand-smoke border-brand-gold/20">
            {statistics?.recent.activities && statistics.recent.activities.length > 0 ? (
              <div className="space-y-3">
                {statistics.recent.activities.map((activity) => {
                  const activityIcons = {
                    order: ShoppingCart,
                    flow: GitBranch,
                    event: Calendar,
                  };
                  const activityColors = {
                    order: 'from-blue-500 to-cyan-600',
                    flow: 'from-purple-500 to-pink-600',
                    event: 'from-green-500 to-emerald-600',
                  };
                  const Icon = activityIcons[activity.type];

                  return (
                    <div
                      key={activity.id}
                      className="flex items-start gap-4 p-4 bg-brand-ink/30 rounded-lg border border-brand-gold/10 hover:bg-brand-ink/50 transition-colors"
                    >
                      <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${activityColors[activity.type]} flex items-center justify-center flex-shrink-0`}>
                        <Icon className="w-5 h-5 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white font-medium text-base">{activity.description}</p>
                        <p className="text-sm text-brand-ivory/60 mt-1">
                          {format(new Date(activity.createdAt), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8 text-brand-ivory/60 text-base">
                <Activity className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>Nenhuma atividade recente</p>
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}
